<template>
  <div class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
    <div class="trezo-card-content relative flex-1">
      <!-- Title with Icon -->
      <div class="flex items-center gap-3 mb-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 dark:from-purple-500 dark:to-indigo-600
                    flex items-center justify-center shadow-lg">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
          </svg>
        </div>
        <span class="block text-sm font-semibold text-slate-600 dark:text-slate-300 tracking-wide uppercase">
          {{ directT('dashboard.crm.annualProfit') }}
        </span>
      </div>

      <!-- Main Profit Value -->
      <div class="mb-2">
        <div v-if="loading" class="flex items-center gap-2">
          <div class="h-8 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse w-32"></div>
        </div>
        <h2 v-else class="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
          {{ currencySymbol }}{{ totalProfit.toLocaleString() }}
        </h2>
      </div>

      <!-- Chart Container -->
      <div class="absolute -top-[28px] ltr:-right-[9px] rtl:-left-[9px] max-w-[120px]">
        <apexchart
          v-if="isClient && monthlyProfits && monthlyProfits.length > 0"
          type="area"
          height="95"
          :options="annualProfit"
          :series="profit"
        ></apexchart>
        <div v-else-if="isClient && loading"
             class="h-[95px] flex items-center justify-center">
          <div class="w-20 h-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
        </div>
      </div>

      <!-- Bottom Stats Section -->
      <div class="mt-[25px] md:mt-[34px] flex items-center justify-between">
        <!-- Growth Percentage -->
        <span
          v-if="growthPercentage !== null"
          class="inline-flex items-center gap-1 text-sm font-semibold py-2 px-3 rounded-xl
                 transition-all duration-300 shadow-sm"
          :class="growthPercentage >= 0 ? 'text-success-700 border-success-300 bg-success-100' : 'text-danger-700 border-danger-300 bg-danger-100'"
        >
          <svg v-if="growthPercentage >= 0" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12"></path>
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13l-5 5m0 0l-5-5m5 5V6"></path>
          </svg>
          {{ growthPercentage >= 0 ? '+' : '' }}{{ growthPercentage }}%
        </span>
        
        <!-- Annual Indicator -->
        <span v-else class="inline-flex items-center gap-1 text-sm font-semibold py-2 px-3 rounded-xl
                     text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30
                     border border-orange-200 dark:border-orange-800 transition-all duration-300 shadow-sm">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {{ directT('dashboard.annual') }}
        </span>

        <!-- Time Period -->
        <span class="block text-sm font-medium text-slate-500 dark:text-slate-400 tracking-wide">
          {{ directT('dashboard.last12Months') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from "@/lib/supabaseClient";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Deal {
  id: string;
  amount: number | string;
  created_at: string;
  [key: string]: any;
}

interface Organization {
  id: string;
  currency: string;
  [key: string]: any;
}

export default defineComponent({
  name: "AnnualProfitChart",
  setup() {
    const { locale } = useI18n();
    const isClient = ref(false);
    const loading = ref(true);
    const deals = ref<Deal[]>([]);
    const totalProfit = ref(0);
    const growthPercentage = ref(0);
    const currencySymbol = ref('$');
    const monthlyProfits = ref<number[]>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]); // Last 12 months
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    const profit = computed(() => {
      return [
        {
          name: directT('dashboard.crm.annualProfit'),
          data: monthlyProfits.value,
        },
      ];
    });
    
    // Computed property for chart options to ensure currency symbol is reactive
    const annualProfit = computed(() => {
      return {
        chart: {
          type: "area",
          height: 95,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        colors: ["#C52B09"],
        dataLabels: {
          enabled: false,
        },
        grid: {
          show: false,
          borderColor: "#ECEEF2",
        },
        stroke: {
          curve: "smooth",
          width: 1,
        },
        xaxis: {
          categories: getMonthNames(),
          axisTicks: {
            show: false,
            color: "#B1BBC8",
          },
          axisBorder: {
            show: false,
            color: "#B1BBC8",
          },
          labels: {
            show: false,
            style: {
              colors: "#8695AA",
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          show: false,
          labels: {
            formatter: (val: string) => {
              return currencySymbol.value + val;
            },
            style: {
              colors: "#64748B",
              fontSize: "12px",
            },
          },
        },
        legend: {
          show: false,
          position: "top",
          fontSize: "12px",
          horizontalAlign: "left",
          itemMargin: {
            horizontal: 8,
            vertical: 0,
          },
          labels: {
            colors: "#64748B",
          },
          markers: {
            width: 9,
            height: 9,
            offsetX: -2,
            offsetY: -0.5,
            radius: 2,
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => {
              return currencySymbol.value + val.toLocaleString();
            }
          }
        }
      };
    });

    // Get month names based on locale
    const getMonthNames = () => {
      const months = [];
      const date = new Date();
      
      // Get last 12 months
      for (let i = 11; i >= 0; i--) {
        date.setMonth(date.getMonth() - (i === 11 ? 11 : 1));
        const monthName = date.toLocaleString(locale.value, { month: 'short' });
        months.push(monthName);
      }
      
      // Reset date
      date.setMonth(new Date().getMonth());
      
      return months;
    };

    // Fetch deals data from Supabase
    const fetchDealsData = async () => {
      try {
        loading.value = true;
        
        // Get current user's organization
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.warn('User not authenticated');
          loading.value = false;
          return;
        }
        
        // Get the user's organization ID
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();
        
        if (profileError || !userProfile?.org_id) {
          console.warn('User has no organization');
          loading.value = false;
          return;
        }
        
        const organizationId = userProfile.org_id as string;
        
        // Get organization's currency
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('currency')
          .eq('id', organizationId)
          .single();
          
        if (orgError) {
          console.error('Error fetching organization data:', orgError);
        } else if (orgData && orgData.currency) {
          currencySymbol.value = (orgData as Organization).currency;
        }
        
        // Calculate date range for the last 24 months (12 months for current + 12 months for previous year)
        const today = new Date();
        const twoYearsAgo = new Date(today);
        twoYearsAgo.setFullYear(today.getFullYear() - 2);
        
        // Fetch deals from the last 24 months
        const { data, error } = await supabase
          .from('deals')
          .select('id, amount, created_at')
          .eq('organization_id', organizationId)
          .gte('created_at', twoYearsAgo.toISOString())
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching deals:', error);
          loading.value = false;
          return;
        }
        
        deals.value = data as Deal[];
        
        // Calculate date for 12 months ago
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        
        // Current year total profit
        let currentYearTotal = 0;
        
        // Previous year total profit
        let previousYearTotal = 0;
        
        // Calculate monthly profits for the last 12 months
        const monthlyProfitData: number[] = Array(12).fill(0);
        
        deals.value.forEach(deal => {
          const dealAmount = typeof deal.amount === 'string' ? parseFloat(deal.amount) : deal.amount;
          if (isNaN(dealAmount)) return;
          
          const dealDate = new Date(deal.created_at);
          const monthsAgo = (today.getFullYear() - dealDate.getFullYear()) * 12 + today.getMonth() - dealDate.getMonth();
          
          // Current year (last 12 months)
          if (monthsAgo >= 0 && monthsAgo < 12) {
            currentYearTotal += dealAmount;
            // Add to the appropriate month bucket (0 is the current month, 11 is 11 months ago)
            monthlyProfitData[monthsAgo] += dealAmount;
          } 
          // Previous year (13-24 months ago)
          else if (monthsAgo >= 12 && monthsAgo < 24) {
            previousYearTotal += dealAmount;
          }
        });
        
        // Reverse the array to show oldest month first (for chart display)
        monthlyProfits.value = monthlyProfitData.reverse();
        totalProfit.value = currentYearTotal;
        
        // Calculate growth percentage
        if (previousYearTotal > 0) {
          growthPercentage.value = Math.round(((currentYearTotal - previousYearTotal) / previousYearTotal) * 100);
        } else if (currentYearTotal > 0) {
          growthPercentage.value = 100; // If previous year had 0 profit, growth is 100%
        } else {
          growthPercentage.value = 0; // If both years had 0 profit, growth is 0%
        }
        
      } catch (error) {
        console.error('Error in fetchDealsData:', error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      isClient.value = true;
      fetchDealsData();
    });

    return {
      isClient,
      loading,
      profit,
      annualProfit,
      totalProfit,
      growthPercentage,
      currencySymbol,
      monthlyProfits,
      directT,
    };
  },
});
</script>
